<template>
	<el-form id="property-info" ref="form" :model="property_information" :rules="validation_rules" label-position="top" class="note-card">
		<h1>Property Information</h1>
		<p>
			For assistance with property information, please visit the <a href="http://www.charlestoncounty.org" target="
			">Charleston County website</a>. Under the Online Services section, use the <a href="https://gisccweb.charlestoncounty.org/Public_Search/" target="
			">GIS Parcel Viewer</a> or the <a href="https://www.charlestoncounty.org/departments/rod/ds-DMNameandDateSearch.php" target="
			">ROD Document Search</a>
		</p>
		<div>
			<h2>Landowner's Name</h2>
			<el-form-item prop="landowner.name">
				<el-input v-model="name" type="textarea" :autosize="input_config.textarea_autosize"/>
			</el-form-item>
			<el-form-item prop="landowner.is_entity" label="Is the landowner an LLC, Limited Partnership, or any other entity?">
				<el-radio-group v-model="is_entity">
					<el-radio :label="true">Yes</el-radio>
					<el-radio :label="false">No</el-radio>
				</el-radio-group>
			</el-form-item>
			<el-form-item v-if="is_entity" prop="landowner.business_partners" label="Please provide the names and titles of ALL individuals and / or partners:">
				<el-input v-model="business_partners" type="textarea" :autosize="input_config.textarea_autosize"/>
			</el-form-item>
		</div>
		<hr>
		<div>
			<h2>Landowner's Mailing Address</h2>
			<el-form-item prop="landowner.mailing_address.street_1" label="Street 1">
				<el-input v-model="mailing_street_1"/>
			</el-form-item>
			<el-form-item prop="landowner.mailing_address.street_2" label="Street 2">
				<el-input v-model="mailing_street_2"/>
			</el-form-item>
			<el-row :gutter="20">
				<el-col :span="10">
					<el-form-item prop="landowner.mailing_address.city" label="City">
						<el-input v-model="mailing_city"/>
					</el-form-item>
				</el-col>
				<el-col :span="7">
					<el-form-item prop="landowner.mailing_address.state" label="State">
						<el-input v-model="mailing_state" maxlength="2"/>
					</el-form-item>
				</el-col>
				<el-col :span="7">
					<el-form-item prop="landowner.mailing_address.zip" label="Zip">
						<el-input v-model="mailing_zip"/>
					</el-form-item>
				</el-col>
			</el-row>
		</div>
		<hr>
		<div class="avoid-break">
			<h2>Property Address</h2>
			<p>(if property is not yet addressed list nearest cross streets in street fields)</p>
			<el-form-item prop="property_address.street_1" label="Street 1">
				<el-input v-model="property_street_1"/>
			</el-form-item>
			<el-form-item prop="property_address.street_2" label="Street 2">
				<el-input v-model="property_street_2"/>
			</el-form-item>
			<el-row :gutter="20">
				<el-col :span="8">
					<el-form-item prop="property_address.city" label="City">
						<el-input v-model="property_city"/>
					</el-form-item>
				</el-col>
				<el-col :span="8">
					<el-form-item prop="property_address.state" label="State">
						<el-input v-model="property_state" maxlength="2"/>
					</el-form-item>
				</el-col>
				<el-col :span="8">
					<el-form-item prop="property_address.zip" label="Zip">
						<el-input v-model="property_zip"/>
					</el-form-item>
				</el-col>
			</el-row>
		</div>
		<hr>
		<div class="avoid-break">


			<h2 class="break-before">Jurisdiction</h2>
			<p class="only-on-screen">
				For Reference: &nbsp;<el-link class="button" icon="el-icon-link" target="_blank" href="https://chascogis.maps.arcgis.com/apps/webappviewer/index.html?id=b926cb5f658f4f21a49acf38a0f3b06b">Urban Growth Boundary Map</el-link>
			</p>
			<el-form-item prop="jurisdiction.is_unincorporated_charleston_county" label="Is the property located in unincorporated Charleston County?">
				<el-radio-group v-model="is_unincorporated_charleston_county">
					<el-radio :label="true">Yes</el-radio>
					<el-radio :label="false">No</el-radio>
				</el-radio-group>
			</el-form-item>
			<el-form-item v-if="is_unincorporated_charleston_county === false" prop="jurisdiction.municipality" label="Municipality">
				<el-input v-model="municipality"/>
			</el-form-item>
			<p>Projects located within municipal limits inside of the Urban Growth Boundary require proof of an endorsement for the project from the relevant governing body, which the applicant will upload in the Attachments section of this application. Endorsement must acknowledge amount of funding requested by the applicant and the Parcel ID numbers for the subject property.</p>
		</div>
		<hr>

		<el-form-item prop="jurisdiction.number_of_parcels" label="Number of parcels">
			<el-input v-model="number_of_parcels"/>
		</el-form-item>
		<hr>
		<div class="avoid-break">
			<h2>Acreage Distribution - {{ totalAcreage || 0 }} total</h2>

			<el-row :gutter="20">
				<el-col :span="8">
					<el-form-item prop="acreage_distribution.forested" label="Acres forested" :rules="validation_rules.acreage">
						<el-input v-model.number="forested" type="number" />
					</el-form-item>
				</el-col>
				<el-col :span="8">
					<el-form-item prop="acreage_distribution.cleared" label="Acres cleared / open" :rules="validation_rules.acreage">
						<el-input v-model.number="cleared" type="number"/>
					</el-form-item>
				</el-col>
				<el-col :span="8">
					<el-form-item prop="acreage_distribution.wetlands" label="Acres wetlands" :rules="validation_rules.acreage">
						<el-input v-model.number="wetlands" type="number"/>
					</el-form-item>
				</el-col>
			</el-row>

			<hr>
			<el-form-item prop="tax_map_numbers" label="Charleston County Tax Map #(s)">
				<el-input v-model="tax_map_numbers" type="textarea" :autosize="input_config.textarea_autosize"/>
			</el-form-item>
			<el-row class="form-item-margin-bottom" :gutter="20">
				<el-col :span="8">
					<el-form-item prop="zoning_status" label="Current zoning status">
						<el-input v-model="zoning_status" type="textarea" :autosize="input_config.textarea_autosize"/>
					</el-form-item>
				</el-col>
				<el-col :span="8">
					<el-form-item prop="assessor_plot_lot_numbers" label="Assessor's Plat and Lot Numbers">
						<el-input v-model="assessor_plot_lot_numbers" type="textarea" :autosize="input_config.textarea_autosize"/>
					</el-form-item>
				</el-col>
				<el-col :span="8">
					<el-form-item prop="deed_book_page_number" label="Deed Book and Page Number">
						<el-input v-model="deed_book_page_number" type="textarea" :autosize="input_config.textarea_autosize"/>
					</el-form-item>
				</el-col>
			</el-row>

			<el-form-item prop="land_use_description" label="Describe the current uses on the land">
				<el-input v-model="land_use_description" type="textarea" :autosize="input_config.textarea_autosize"/>
			</el-form-item>
		</div>
		<hr>

		<div class="avoid-break">
			<h2>Site Accessibility</h2>
			<el-form-item prop="site_accessibility.is_road_accessible" label="Is the property accessible by a road?">
				<el-radio-group v-model="is_road_accessible">
					<el-radio :label="true">Yes</el-radio>
					<el-radio :label="false">No</el-radio>
				</el-radio-group>
			</el-form-item>
			<template v-if="is_road_accessible">
				<el-form-item prop="site_accessibility.road_types" label="Types of road">
					<el-checkbox-group v-model="road_types">
						<el-checkbox :label="0">Unimproved</el-checkbox>
						<el-checkbox :label="1">Improved</el-checkbox>
					</el-checkbox-group>
				</el-form-item>
				<el-form-item v-if="road_types.includes(0)" prop="site_accessibility.unimproved_roads" label="Unimproved road name(s)">
					<el-input v-model="unimproved_roads"/>
				</el-form-item>
				<el-form-item v-if="road_types.includes(1)" prop="site_accessibility.improved_roads" label="Improved road name(s)">
					<el-input v-model="improved_roads"/>
				</el-form-item>
			</template>
		</div>
		<hr>
		<div class="avoid-break">
			<h2>Water Access</h2>
			<el-form-item prop="water_access.has_water_access" label="Does the property have access to water?">
				<el-radio-group v-model="has_water_access">
					<el-radio :label="true">Yes</el-radio>
					<el-radio :label="false">No</el-radio>
				</el-radio-group>
			</el-form-item>
			<template v-if="has_water_access">
				<el-form-item prop="water_access.types_of_access" label="Types of access">
					<el-checkbox-group v-model="types_of_access">
						<el-checkbox :label="0">Pond</el-checkbox>
						<el-checkbox :label="1">Ocean</el-checkbox>
						<el-checkbox :label="2">Freshwater stream / creek</el-checkbox>
						<el-checkbox :label="3">Tidal Creek</el-checkbox>
						<el-checkbox :label="4">Deep River</el-checkbox>
					</el-checkbox-group>
				</el-form-item>
				<el-form-item v-if="types_of_access.includes(2)" prop="water_access.freshwater_stream_creek_names" label="Freshwater stream / creek name(s)">
					<el-input v-model="freshwater_stream_creek_names"/>
				</el-form-item>
				<el-form-item v-if="types_of_access.includes(3)" prop="water_access.tidal_creek_names" label="Tidal creek name(s)">
					<el-input v-model="tidal_creek_names"/>
				</el-form-item>
				<el-form-item v-if="types_of_access.includes(4)" prop="water_access.deep_river_names" label="Deep river name(s)">
					<el-input v-model="deep_river_names"/>
				</el-form-item>

			</template>
		</div>
	</el-form>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import { mapSimpleValidation } from '@/js/utilities/validation-mapper.js';
import validation_mixin from '@/js/mixins/validation-mixin.js';
export default {
	mixins: [validation_mixin],
	computed: {
		...mapGetters('application/property_information', ['totalAcreage']),
		...mapState('application/property_information', {
			property_information: state => state
		}),
		...mapFields('application/property_information', [
			'landowner.name', 'landowner.is_entity', 'landowner.business_partners', 'jurisdiction.is_unincorporated_charleston_county', 'jurisdiction.municipality', 'jurisdiction.number_of_parcels', 'acreage_distribution.forested', 'acreage_distribution.cleared', 'acreage_distribution.wetlands', 'acreage_distribution.total', 'tax_map_numbers', 'zoning_status', 'assessor_plot_lot_numbers', 'deed_book_page_number', 'land_use_description', 'site_accessibility.is_road_accessible', 'site_accessibility.road_types', 'site_accessibility.unimproved_roads', 'site_accessibility.improved_roads', 'water_access.has_water_access', 'water_access.types_of_access', 'water_access.freshwater_stream_creek_names', 'water_access.tidal_creek_names', 'water_access.deep_river_names'
		]),
		...mapFields('application/property_information', {
			mailing_street_1: 'landowner.mailing_address.street_1',
			mailing_street_2: 'landowner.mailing_address.street_2',
			mailing_city: 'landowner.mailing_address.city',
			mailing_state: 'landowner.mailing_address.state',
			mailing_zip: 'landowner.mailing_address.zip',
			property_street_1: 'property_address.street_1',
			property_street_2: 'property_address.street_2',
			property_city: 'property_address.city',
			property_state: 'property_address.state',
			property_zip: 'property_address.zip'
		})
	},
	data(){
		return {
			validation_rules: {
				...mapSimpleValidation([
					'landowner.name', 'landowner.is_entity', 'landowner.business_partners', 'jurisdiction.is_unincorporated_charleston_county', 'jurisdiction.municipality', 'jurisdiction.number_of_parcels', 'tax_map_numbers', 'zoning_status', 'assessor_plot_lot_numbers', 'deed_book_page_number', 'land_use_description', 'site_accessibility.is_road_accessible', 'site_accessibility.road_types', 'site_accessibility.unimproved_roads', 'site_accessibility.improved_roads', 'water_access.has_water_access', 'water_access.types_of_access', 'water_access.freshwater_stream_creek_names', 'water_access.tidal_creek_names', 'water_access.deep_river_names', 'landowner.mailing_address.street_1', 'landowner.mailing_address.city', 'landowner.mailing_address.state', 'landowner.mailing_address.zip', 'property_address.street_1', 'property_address.city', 'property_address.state', 'property_address.zip'
				]),
				acreage: [
					{ required: true, message: 'This is required', trigger: 'input' },
					// { type:Number, message:'Value must be a number', trigger:'input'},
					{ validator: this.acreageChecker, trigger: 'input' }
				]
			}
		}
	},
	methods: {
		acreageChecker(rule, value, callback){
			const { forested, cleared, wetlands } = this;
			if(forested + cleared + wetlands <= 0){
				callback(new Error('All three types can not be 0'));
			} else {
				callback();
			}
		}
	}
}
</script>

<style lang="scss">

#printable .printable{
	.form-item-margin-bottom{
		div.el-form-item{
			margin-bottom:12px !important;
		}
	}
}

#property-info{
	.el-input-number{
		width: 100%;
	}
}

.el-link.button{
	border:1px solid lightgray;
	border-radius:8px;
	padding:7px 15px;
	margin-left:7px;
}

</style>
